// src/Component/Testimonials.tsx
import React from 'react';
import { Star, Quote, ShieldCheck, BadgeCheck, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

const Testimonials: React.FC = () => {
  // Real-looking Indian stock market traders & investors
  const reviews = [
    {
      name: "Rajesh Khandelwal",
      title: "BankNifty Options Trader",
      rating: 5,
      feedback: "Motilal Algo has completely changed how I trade options. The zero-latency execution means my algo fires exactly at the breakout. Highly recommended for serious F&O traders.",
      platform: "Verified User",
      icon: <BadgeCheck size={14} className="text-blue-400" />,
      tagColor: "bg-blue-500/10 text-blue-400 border-blue-500/20"
    },
    {
      name: "Priya Sharma",
      title: "Equity Investor",
      rating: 5,
      feedback: "I shifted my entire portfolio here. Zero AMC and the NISM certified research calls are incredibly accurate. It feels very safe and trusted.",
      platform: "Verified Investor",
      icon: <ShieldCheck size={14} className="text-emerald-400" />,
      tagColor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    },
    {
      name: "Amit Deshmukh",
      title: "Intraday Scalper",
      rating: 4,
      feedback: "The 4X margin for intraday is a game-changer. The charts never lag, and the UI is buttery smooth. Best broker platform I've used in 5 years.",
      platform: "Pro Trader",
      icon: <TrendingUp size={14} className="text-amber-400" />,
      tagColor: "bg-amber-500/10 text-amber-400 border-amber-500/20"
    },
    {
      name: "Neha Gupta",
      title: "Swing Trader",
      rating: 5,
      feedback: "Flexible brokerage plans saved me thousands in taxes and fees this year. The 'Call and Trade' offline support team in Mumbai is also very responsive.",
      platform: "Verified User",
      icon: <BadgeCheck size={14} className="text-blue-400" />,
      tagColor: "bg-blue-500/10 text-blue-400 border-blue-500/20"
    },
    {
        name: "Vikram Singh",
        title: "Algo Enthusiast",
        rating: 5,
        feedback: "Setting up my own APIs with Motilal Algo was seamless. The margin funding feature allows me to scale my winning strategies effortlessly. A+ service.",
        platform: "API Partner",
        icon: <ShieldCheck size={14} className="text-emerald-400" />,
        tagColor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    },
    {
        name: "Ananya Reddy",
        title: "Portfolio Manager",
        rating: 5,
        feedback: "Managing multiple client accounts is so easy here. The transparency in their system and strict adherence to SEBI circulars gives ultimate peace of mind.",
        platform: "Institutional",
        icon: <TrendingUp size={14} className="text-purple-400" />,
        tagColor: "bg-purple-500/10 text-purple-400 border-purple-500/20"
    },
  ];

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { type: "spring", stiffness: 60, damping: 20 }
    }
  };

  return (
    <section className="relative py-20 md:py-24 bg-[#060a14] overflow-hidden font-[Urbanist]">
      
      {/* 1. Background Ambience (Deep Premium Look) */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle grid pattern for trading vibe */}
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="absolute top-[10%] right-[10%] w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-[10%] left-[10%] w-[400px] h-[400px] bg-emerald-600/10 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 relative z-10">
        
        {/* 2. Header Section */}
        <div className="text-center mb-16 md:mb-20 space-y-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-slate-800/50 text-gray-300 text-sm font-bold tracking-wide border border-gray-700 backdrop-blur-sm"
          >
            <ShieldCheck size={16} className="text-emerald-400" />
            Trusted by 50,000+ Indian Traders
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight"
          >
            Don't just take our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">word for it</span>
          </motion.h2>
        </div>

        {/* 3. Testimonials Grid (Sleek & Compact) */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {reviews.map((review, index) => (
            <motion.div
              key={index}
              // variants={cardVariants}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
              className="group relative bg-[#0f1523]/80 backdrop-blur-xl border border-gray-800 hover:border-gray-600 rounded-2xl p-6 flex flex-col justify-between transition-all duration-300 shadow-xl"
            >
              
              {/* Decorative Large Quote Icon (Subtle Background) */}
              <div className="absolute top-4 right-5 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                <Quote size={60} className="text-white fill-white transform rotate-180" />
              </div>

              {/* User Feedback */}
              <div className="relative z-10 flex-1">
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={14} fill="currentColor" className="text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-6 font-medium">
                  "{review.feedback}"
                </p>
              </div>

              {/* Footer: User Info & Platform Badge */}
              <div className="flex items-center justify-between pt-5 border-t border-gray-800/60 mt-auto relative z-10">
                <div className="flex items-center gap-3">
                  {/* Avatar - Indian Initial Letter */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-inner bg-gradient-to-br ${
                      index % 3 === 0 ? 'from-blue-600 to-indigo-800' : index % 3 === 1 ? 'from-emerald-500 to-teal-700' : 'from-rose-500 to-orange-700'
                  }`}>
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-slate-100 font-bold text-sm">{review.name}</p>
                    <p className="text-slate-500 text-xs font-semibold">{review.title}</p>
                  </div>
                </div>

                {/* Platform/Trust Badge */}
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] font-extrabold uppercase tracking-wider ${review.tagColor}`}>
                  {review.icon}
                  {review.platform}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;